// This exists to work around complaints from TypeScript that Element does not have these attributes.
interface Element {
  offsetTop: number;
  offsetLeft: number;
  offsetHeight: number;
  offsetWidth: number;
}

// See the Gitlab wiki for documentation on this directive.
export default function MrStickyDirective() {
  'ngInject';

  function scrollHandler() {
    // Figure out if we need to add the sticky class and the placeholder to the page or remove them both. There is of
    // course a third option where we do not change the current state of the page. That should happen much more than the
    // other two.
    if (
      !this.placeholderAdded &&
      this.makeSticky &&
      window.pageYOffset + this.topPadding >= this.stickyElement.offsetTop
    ) {
      // The item could have changed height. If so, the height of the placeholder should change to avoid a big jump.
      if (this.stickyElement.offsetHeight !== this.placeholder.offsetHeight) {
        this.placeholder.style.height = this.stickyElement.offsetHeight + 'px';
      }

      // Add the class which will make our element float above the page. This takes it out of the flow of the page
      // rendering and would cause other elements to jump around and re-render their positions.
      this.stickyElement.classList.add(this.stickyClass);

      // Except that we insert a placeholder right before it on the page which, hopefully, is exactly the same size as
      // the element we just removed from the page flow. Thus nothing else on the page should change position as we
      // make our sticky thing float and not float.
      this.stickyElement.parentNode.insertBefore(
        this.placeholder,
        this.stickyElement
      );
      this.placeholderAdded = true;
    } else if (
      this.placeholderAdded &&
      (!this.makeSticky ||
        window.pageYOffset + this.topPadding <= this.placeholder.offsetTop)
    ) {
      // This reverses what we did before. Remove the class which makes us float above the page.
      this.stickyElement.classList.remove(this.stickyClass);

      // Then remove the placeholder element so nothing on the page jumps.
      this.stickyElement.parentNode.removeChild(this.placeholder);
      this.placeholderAdded = false;
    }
  }

  return {
    scope: {
      data: '=mrSticky'
    },
    controller: function ($scope, $element, $timeout) {
      'ngInject';

      // The code gets the size of the item we're preventing from scrolling off the page and it creates a dummy
      // element of the same size. Then when the sticky element is fixed in place, it will get removed from the
      // main flow of the page and cause a jerk in everything after it when it is removed. Except this code will
      // automatically swap in the dummy element to prevent that sudden jerk and resize the placeholder if the size
      // of what it's replacing changes.
      this.stickyElement = $element[0];
      this.placeholder = document.createElement('div');
      this.placeholderAdded = false;

      this.stickyClass =
        $scope.data && $scope.data.stickyClass
          ? $scope.data.stickyClass
          : 'mr_sticky';
      this.topPadding =
        $scope.data && $scope.data.padding ? $scope.data.padding : 0;
      this.makeSticky =
        $scope.data && 'makeSticky' in $scope.data
          ? $scope.data.makeSticky
          : true;

      // This tracks the expressions which give us the class and top padding. We're allowing them to be AngularJS for a
      // reason, they might change after we've already made things sticky. If so, this will adjust them when they do.
      $scope.$watch('data', (newValue, oldValue) => {
        if (newValue !== oldValue) {
          this.stickyClass =
            $scope.data && $scope.data.stickyClass
              ? $scope.data.stickyClass
              : 'mr_sticky';
          this.topPadding =
            $scope.data && $scope.data.padding ? $scope.data.padding : 0;
          this.makeSticky =
            $scope.data && 'makeSticky' in $scope.data
              ? $scope.data.makeSticky
              : true;
        }
      });

      // Bind "this" to the function because we always want this particular event listener to point at this instance of
      // Mr. Sticky. Other instances on the same page will have their own bound event listeners independent of this one.
      let eventListener = scrollHandler.bind(this);

      // Man I hate resorting to a timeout to get this information, but there seems to be little choice. Let me
      // tell you why.
      //
      // When you change routes on an AngularJS page with multiple routes, ngView first loads the new view up below
      // the existing view in the DOM. Then it removes the old view. It takes a fraction of a second and, as Richard
      // said, "It makes a lot of sense, you don't get a flash of an empty page as you switch from one route to another."
      //
      // No doubt another reason for doing it is when you are using AngularJS animations and you're animating the
      // transition between routes. In that case, both must be available for that to work.
      //
      // Unfortunately, it totally breaks Mr. Sticky for things like the Sub-Nav. The Sub-Nav loads very quickly and
      // it used to read its position on the page and get a number like 2000px or something. That was because it was
      // still, very briefly, below the previous view before it went away on a route change. Then the old view
      // disappears and the location is suddenly totally wrong. Thus it won't stick at the right place at all.
      //
      // That's why this code waits for Angular to finish its rendering and then comes in and finally reads the
      // location of the sticky element then.
      //
      // P.S. I have not tested this code with Angular's ability to animate the transition from one route to another
      // yet. This code may break when the animation takes more than a few milliseconds.
      $timeout(eventListener, 100);

      // This watches for scroll events and swaps in and out the placeholder as needed. It also adds and removes the
      // class on the element we want to be sticky so it will become fixed and at a specific spot on the page when
      // needed.
      window.addEventListener('scroll', eventListener);

      // This is crucial code because we don't want to leave listeners wired up after we've left one route and destroyed
      // a bunch of DOM elements and created new ones. They need to be unhooked.
      $scope.$on('$destroy', function (event) {
        window.removeEventListener('scroll', eventListener);
      });
    }
  };
}
